import { 
  Vector3, 
  Vector2, 
  Raycaster, 
  CatmullRomCurve3, 
  BufferGeometry, 
  LineBasicMaterial, 
  Line, 
  MeshStandardMaterial, 
  Mesh, 
  DoubleSide,
  TubeGeometry,
  LineCurve3,
  ExtrudeGeometry,
  Shape,
  Float32BufferAttribute,
  Box3,
  Matrix4
} from "three";

/**
 * 道路工具类
 * 用于通过鼠标交互在地形上绘制道路
 */
export class RoadToolVR {
  constructor(manager) {
    this.manager = manager;
    this.enabled = false;
    this.isDrawing = false;
    
    this.inputLocked = false;

    //VR控制器相关
    this.controllers=[];
    this.activeController=null;
    
    // 道路参数
    this.roadWidth = 2.0;        // 道路宽度
    this.roadType = 'curve';      // 道路类型: 'curve'曲线, 'line'直线
    this.roadMaterial = null;     // 道路材质
    this.roadHeightOffset = 0.05; // 道路高度偏移，避免z-fighting
    
    // 射线拾取
    this.raycaster = new Raycaster();
    
    // 地形对象缓存
    this.terrainMeshes = [];
    
    // 地形高度缓存
    this.heightCache = {
      enabled: false,           // 是否启用高度缓存
      data: new Map(),         // 高度数据缓存 Map<terrainId, {resolution, minX, maxX, minZ, maxZ, heights}>
      resolution: 0.5,         // 缓存分辨率（网格大小）
      dirty: true              // 缓存是否需要更新
    };
    
    // 鼠标状态
    this.lastMousePosition = new Vector2();
    this.currentMousePosition = new Vector2();
    
    // 道路点集合
    this.roadPoints = [];
    
    // 临时显示对象
    this.previewLine = null;      // 当前正在绘制的预览线
    this.previewPoints = [];      // 当前预览线的点
    this.fixedLines = [];         // 已经固定的线段集合
    
    // 已创建的道路对象
    this.roads = [];
    
    // 创建默认材质
    this.createDefaultMaterial();
  }

  // 初始化VR控制器
  initVRControllers() {
    const renderer = this.manager.sceneManager.renderer;
    const scene = this.manager.sceneManager.scene;

    for (let i = 0; i < 2; i++) {
      const controller = renderer.xr.getController(i);
      controller.userData.index = i;

      controller.addEventListener('selectstart', (e) => {
        this.onSelectStart(e, controller);
      });

      controller.addEventListener('selectend', (e) => {
        this.onSelectEnd(e, controller);
      });

      scene.add(controller);
      this.controllers.push(controller);
    }
  }


  /**
   * 创建默认道路材质
   */
  createDefaultMaterial() {
    // 创建一个明显的道路材质
    this.roadMaterial = new MeshStandardMaterial({
      color: 0xffffff,      // 深灰色
      roughness: 0.7,       // 较粗糙
      metalness: 0.1,       // 低金属感
      side: DoubleSide,     // 双面渲染
      flatShading: false    // 平滑着色
    });
    
    // 确保材质已创建并更新
    this.roadMaterial.needsUpdate = true;
  }
  
  /**
   * 设置道路颜色
   * @param {number} color - 道路颜色，十六进制格式，如0x333333
   */
  setRoadColor(color) {
    if (!this.roadMaterial) {
      this.createDefaultMaterial();
    }
    
    this.roadMaterial.color.setHex(color);
    this.roadMaterial.needsUpdate = true;
    
    // 更新已创建的道路颜色
    this.roads.forEach(road => {
      if (road.material) {
        road.material.color.setHex(color);
        road.material.needsUpdate = true;
      }
    });
    
    console.log(`道路颜色已设置为: ${color.toString(16)}`);
  }
  
  /**
   * 设置道路高度偏移
   * @param {number} offset - 道路高度偏移值
   */
  setRoadHeightOffset(offset) {
    this.roadHeightOffset = offset;
    console.log(`道路高度偏移已设置为: ${offset}`);
  }

  /**
   * 创建道路网格
   */
  createRoadMesh() {
    if (this.roadPoints.length < 2) return;
    
    let points;
    
    // 根据道路类型创建点集
    if (this.roadType === 'curve' && this.roadPoints.length > 2) {
      // 创建曲线
      const curve = new CatmullRomCurve3(this.roadPoints);
      points = curve.getPoints(Math.max(50, this.roadPoints.length * 20));
    } else {
      // 直线模式 - 在每两个点之间创建直线段，并贴合地形
      points = [];
      for (let i = 0; i < this.roadPoints.length - 1; i++) {
        const start = this.roadPoints[i];
        const end = this.roadPoints[i + 1];
        const segmentPoints = 100; // 每段直线的点数
        
        for (let j = 0; j <= segmentPoints; j++) {
          const t = j / segmentPoints;
          const x = start.x + (end.x - start.x) * t;
          // 水平方向插值
          const z = start.z + (end.z - start.z) * t;
          
          // 使用射线检测获取该点在地形上的高度
          const y = this.getTerrainHeightAt(x, z, start.y);
          
          // 避免重复点
          if (i > 0 && j === 0) continue;
          
          points.push(new Vector3(x, y, z));
        }
      }
    }
    
    // 道路宽度
    //const roadWidth = this.roadWidth;
    const roadWidth=0.5;
    
    // 创建道路几何体
    const roadGeometry = new BufferGeometry();
    const vertices = [];
    const uvs = [];
    const indices = [];
    
    // 为每个点创建横截面
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      
      // 计算切线方向
      const tangent = new Vector3();
      if (i < points.length - 1) {
        tangent.subVectors(points[i + 1], point).normalize();
      } else {
        tangent.subVectors(point, points[i - 1]).normalize();
      }
      
      // 计算横截面方向（垂直于切线）
      const up = new Vector3(0, 1, 0);
      const right = new Vector3().crossVectors(tangent, up).normalize();
      
      // 创建道路两侧的点
      const leftPoint = new Vector3().copy(point).addScaledVector(right, -roadWidth / 2);
      const rightPoint = new Vector3().copy(point).addScaledVector(right, roadWidth / 2);
      
      // 添加顶点
      vertices.push(leftPoint.x, leftPoint.y, leftPoint.z);
      vertices.push(rightPoint.x, rightPoint.y, rightPoint.z);
      
      // 添加UV坐标
      const v = i / (points.length - 1);
      uvs.push(0, v);
      uvs.push(1, v);
      
      // 添加面索引（除了最后一个点）
      if (i < points.length - 1) {
        const baseIndex = i * 2;
        indices.push(baseIndex, baseIndex + 1, baseIndex + 2);
        indices.push(baseIndex + 1, baseIndex + 3, baseIndex + 2);
      }
    }
    
    // 设置几何体属性
    roadGeometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    roadGeometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    roadGeometry.setIndex(indices);
    roadGeometry.computeVertexNormals();
    
    // 使用已有的道路材质或创建新材质
    if (!this.roadMaterial) {
      this.createDefaultMaterial();
    }
    
    // 创建道路网格
    this.roadMesh = new Mesh(roadGeometry, this.roadMaterial.clone());
    this.roadMesh.name = 'road';
    this.roadMesh.castShadow = true;
    this.roadMesh.receiveShadow = true;
    
    // 应用高度偏移，避免与地形的z-fighting
    this.roadHeightOffset=0.05;
    this.roadMesh.position.y += this.roadHeightOffset;
    
    // 添加到场景
    this.manager.sceneManager.scene.add(this.roadMesh);
    
    // 清除预览线
    this.clearPreview();
    
    // 清除道路点
    this.roadPoints = [];
  }

  /**
   * 设置道路类型
   * @param {string} type - 'curve'曲线或'line'直线
   */
  setRoadType(type) {
    if (['curve', 'line'].includes(type)) {
      this.roadType = type;
      console.log(`道路类型已设置为: ${type}`);
      
      // 如果正在绘制，更新预览线
      if (this.isDrawing && this.previewPoints.length >= 2) {
        this.updatePreviewLine();
      }
    }
  }
  
  /**
   * 设置道路宽度
   * @param {number} width - 道路宽度
   */
  setRoadWidth(width) {
    if (width > 0) {
      this.roadWidth = width;
      console.log(`道路宽度已设置为: ${width}`);
      
      // 如果正在绘制，更新预览线
      if (this.isDrawing && this.previewPoints.length >= 2) {
        this.updatePreviewLine();
      }
    }
  }

  /**
   * 鼠标按下事件
   */
  onPointerDown(event) {
    if (!this.enabled) return;
    
    // 右键点击时取消道路绘制
    if (event.button === 2) { // 2 表示鼠标右键
        console.log()
      if (this.isDrawing) {
        this.isDrawing = false;
        this.roadPoints = [];
        this.previewPoints = [];
        this.clearAllLines();
        console.log("已取消道路创建");
      }
      return;
    }
    
    // 只有鼠标左键按下时才开始绘制
    if (event.button === 0) { // 0 表示鼠标左键
      this.updateMousePosition(event);
      
      // 获取地形上的点
      const hitPoint = this.getHitPoint();
      if (hitPoint) {
        if (!this.isDrawing) {
          // 开始新的道路绘制
          this.isDrawing = true;
          this.roadPoints = [];
          this.previewPoints = [];
          this.clearAllLines();
        } else if (this.previewPoints.length >= 2) {
          // 如果已经有预览线，将其固定
          this.fixCurrentPreviewLine();
        }
        
        // 添加点到道路中
        this.roadPoints.push(hitPoint.clone());
        
        // 开始新的预览线
        this.previewPoints = [hitPoint.clone()];
        
        // 更新预览线（此时只有一个点，不会显示线）
        this.clearPreview();
      }
    }
  }
  
  /**
   * 将当前预览线固定为永久线段
   */
  fixCurrentPreviewLine() {
    if (this.previewLine && this.previewPoints.length >= 2) {
      // 将当前预览线添加到固定线段集合中
      this.fixedLines.push(this.previewLine);
      
      // 清空预览线引用（但不从场景中移除）
      this.previewLine = null;
    }
  }

  /**
   * 鼠标移动事件
   */
  onPointerMove(event) {
    if (!this.enabled) return;

    this.updateMousePosition(event);
    
    // 如果正在绘制，更新预览
    if (this.isDrawing && this.previewPoints.length > 0) {
      const hitPoint = this.getHitPoint();
      if (hitPoint) {
        // 如果只有一个点，添加第二个点
        if (this.previewPoints.length === 1) {
          this.previewPoints.push(hitPoint.clone());
        } else {
          // 更新最后一个预览点为当前鼠标位置
          this.previewPoints[this.previewPoints.length - 1] = hitPoint.clone();
        }
        
        // 更新预览线，确保它贴合地形
        this.updatePreviewLine();
      }
    }
  }

  /**
   * 鼠标抬起事件
   */
  onPointerUp(event) {
    if (!this.enabled) return;

    // 鼠标右键抬起时取消道路绘制
    if (event.button === 2) { // 2 表示鼠标右键
      if (this.isDrawing) {
        this.isDrawing = false;
        this.roadPoints = [];
        this.previewPoints = [];
        this.clearPreview();
        console.log("已取消道路创建");
      }
    }
  }

  /**
   * 设置绘制模式
   * @param {string} mode - 'line' 或 'curve'
   */
  setDrawMode(mode) {
    this.setRoadType(mode);
  }

  /**
   * 确认当前道路
   */
  confirmRoad() {
    this.finishRoad();
  }

  /**
   * 取消当前道路
   */
  cancelRoad() {
    this.isDrawing = false;
    this.clearPreview();
  }

  /**
   * 双击事件 - 完成道路绘制
   */
  onDoubleClick(event) {
    if (!this.enabled || !this.isDrawing) return;
    
    if (this.roadPoints.length >= 2) {
      this.finishRoad();
    } else {
      // 如果点数不足，清除当前绘制状态
      this.isDrawing = false;
      this.roadPoints = [];
      this.previewPoints = [];
      this.clearAllLines();
    }
  }

  /**
   * 更新鼠标位置
   */
  updateMousePosition(event) {
    const rect = this.manager.sceneManager.renderer.domElement.getBoundingClientRect();
    this.currentMousePosition.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  }

  /**
   * 获取VR射线与地形的交点
   */
  getHitPointFromController(controller) {
    if (!controller || this.inputLocked) return null;

    if (this.terrainMeshes.length === 0) {
      this.updateTerrainMeshes();
      if (this.terrainMeshes.length === 0) return null;
    }

    console.log("地形模型",this.terrainMeshes);

    const tempMatrix = new Matrix4();
    controller.updateMatrixWorld();
    tempMatrix.identity().extractRotation(controller.matrixWorld);
    this.raycaster.ray.origin.setFromMatrixPosition(this.activeController.matrixWorld);
    this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);
    
    // 检测与地形的交点
    const intersects = this.raycaster.intersectObjects(this.terrainMeshes, false);
    
    if (intersects.length === 0) return null;

    return intersects[0].point;
  }

  onSelectStart(controller) {
    if (!this.enabled || this.inputLocked) return;

    this.activeController = controller;

    const hitPoint = this.getHitPointFromController(controller);
    console.log("手柄点击地面",hitPoint);
    if (!hitPoint) return;

    if (!this.isDrawing) {
      this.isDrawing = true;
      this.roadPoints = [];
      this.previewPoints = [];
      this.clearAllLines();
    } else if (this.previewPoints.length >= 2) {
      this.fixCurrentPreviewLine();
    }

    this.roadPoints.push(hitPoint.clone());
    this.previewPoints = [hitPoint.clone()];
    //this.clearPreview();
    this.updatePreviewLineVR();
  }

  onSelectEnd(event, controller) {
    // 目前不需要处理
  }
  
  /**
   * 获取地形上指定位置的高度
   * @param {number} x - x坐标
   * @param {number} z - z坐标
   * @param {number} defaultY - 如果没有找到地形，则使用的默认高度
   * @returns {number} 地形高度
   */
  getTerrainHeightAt(x, z, defaultY) {
    if (this.terrainMeshes.length === 0) {
      this.updateTerrainMeshes();
      if (this.terrainMeshes.length === 0) return defaultY;
    }
    
    // 如果启用了高度缓存且缓存需要更新，则预计算高度
    if (this.heightCache.enabled && this.heightCache.dirty) {
      this.precomputeTerrainHeights();
    }
    
    // 如果启用了高度缓存，尝试从缓存中获取高度
    if (this.heightCache.enabled && this.heightCache.data.size > 0) {
      // 遍历所有地形的高度缓存
      for (const [terrainId, cache] of this.heightCache.data.entries()) {
        // 检查点是否在当前地形的边界内
        if (x >= cache.minX && x <= cache.maxX && z >= cache.minZ && z <= cache.maxZ) {
          // 计算在高度网格中的索引
          const gridX = Math.floor((x - cache.minX) / cache.resolution);
          const gridZ = Math.floor((z - cache.minZ) / cache.resolution);
          
          // 确保索引在有效范围内
          if (gridX >= 0 && gridX < cache.width && gridZ >= 0 && gridZ < cache.height) {
            const index = gridX + gridZ * cache.width;
            return cache.heights[index];
          }
        }
      }
    }
    
    // 如果缓存中没有找到高度，使用射线检测
    const rayOrigin = new Vector3(x, 1000, z); // 从高处向下射线
    const rayDirection = new Vector3(0, -1, 0); // 向下的方向
    this.raycaster.set(rayOrigin, rayDirection);
    
    // 检测与地形的交点
    const intersects = this.raycaster.intersectObjects(this.terrainMeshes, false);
    if (intersects.length === 0) return defaultY;
    
    // 返回交点的y坐标（高度）
    return intersects[0].point.y;
  }
  
  /**
   * 更新地形网格列表
   */
  updateTerrainMeshes() {
    // 获取场景中的地形对象
    this.terrainMeshes = [];
    this.manager.sceneManager.scene.traverse((object) => {
        object.traverse(child=>{
            if (child.isMesh && object.name.includes('地形')) {
                this.terrainMeshes.push(child);
            }
        })
    });
    // 标记高度缓存需要更新
    this.heightCache.dirty = true;
    
    // 如果启用了高度缓存，预计算地形高度
    if (this.heightCache.enabled && this.terrainMeshes.length > 0) {
      this.precomputeTerrainHeights();
    }
  }
  
  /**
   * 预计算地形高度并缓存
   * 这将显著提高道路绘制时的性能
   */
  precomputeTerrainHeights() {
    console.log("预计算地形高度...");
    
    // 清除旧的缓存
    this.heightCache.data.clear();
    
    // 为每个地形网格创建高度缓存
    for (const terrain of this.terrainMeshes) {
      // 计算地形的边界框
      const box = new Box3().setFromObject(terrain);
      const minX = box.min.x;
      const maxX = box.max.x;
      const minZ = box.min.z;
      const maxZ = box.max.z;
      
      // 计算网格尺寸
      const width = Math.ceil((maxX - minX) / this.heightCache.resolution);
      const height = Math.ceil((maxZ - minZ) / this.heightCache.resolution);
      
      // 创建高度数据数组
      const heights = new Float32Array(width * height);
      
      // 创建射线用于高度采样
      const raycaster = new Raycaster();
      
      // 采样每个网格点的高度
      for (let x = 0; x < width; x++) {
        for (let z = 0; z < height; z++) {
          // 计算世界坐标
          const worldX = minX + x * this.heightCache.resolution;
          const worldZ = minZ + z * this.heightCache.resolution;
          
          // 从高处向下发射射线
          const rayOrigin = new Vector3(worldX, 1000, worldZ);
          const rayDirection = new Vector3(0, -1, 0);
          raycaster.set(rayOrigin, rayDirection);
          
          // 检测与地形的交点
          const intersects = raycaster.intersectObject(terrain, false);
          
          // 存储高度值
          if (intersects.length > 0) {
            heights[x + z * width] = intersects[0].point.y;
          } else {
            // 如果没有交点，使用地形的最低高度
            heights[x + z * width] = box.min.y;
          }
        }
      }
      
      // 存储地形的高度缓存
      this.heightCache.data.set(terrain.uuid, {
        resolution: this.heightCache.resolution,
        minX: minX,
        maxX: maxX,
        minZ: minZ,
        maxZ: maxZ,
        width: width,
        height: height,
        heights: heights
      });
    }
    
    // 标记缓存已更新
    this.heightCache.dirty = false;
    console.log("地形高度预计算完成");
  }
  
  /**
   * 启用道路工具
   */
  enable() {
    this.enabled = true;
    console.log("道路工具已启用");
    
    // 更新地形网格列表
    this.updateTerrainMeshes();
  }
  
  /**
   * 禁用道路工具
   */
  disable() {
    this.enabled = false;
    console.log("道路工具已禁用");
    
    // 清除所有线段
    this.clearAllLines();
    
    // 重置状态
    this.isDrawing = false;
    this.roadPoints = [];
    this.previewPoints = [];
  }

  /**
   * 更新预览线
   */
  updatePreviewLine() {
    // 清除旧的预览线
    this.clearPreview();
    
    if (this.previewPoints.length < 2) return;
    
    let points;
    
    if (this.roadType === 'curve' && this.previewPoints.length > 2) {
      // 使用样条曲线
      const curve = new CatmullRomCurve3(this.previewPoints);
      // 获取曲线上的点
      const curvePoints = curve.getPoints(Math.max(20, this.previewPoints.length * 10));
      
      // 对曲线上的每个点进行地形高度采样
      points = [];
      for (const point of curvePoints) {
        // 获取地形高度
        const y = this.getTerrainHeightAt(point.x, point.z, point.y);
        points.push(new Vector3(point.x, y, point.z));
      }
    } else {
      // 使用直线段，并贴合地形
      points = [];
      for (let i = 0; i < this.previewPoints.length - 1; i++) {
        const start = this.previewPoints[i];
        const end = this.previewPoints[i + 1];
        const segmentPoints = 30; // 每段直线的点数
        
        for (let j = 0; j <= segmentPoints; j++) {
          const t = j / segmentPoints;
          const x = start.x + (end.x - start.x) * t;
          // 水平方向插值
          const z = start.z + (end.z - start.z) * t;
          
          // 使用射线检测获取该点在地形上的高度
          const y = this.getTerrainHeightAt(x, z, start.y);
          
          // 避免重复点
          if (i > 0 && j === 0) continue;
          
          points.push(new Vector3(x, y, z));
        }
      }
    }
    
    // 创建预览线几何体
    const geometry = new BufferGeometry().setFromPoints(points);
    
    // 创建预览线材质和对象
    const material = new LineBasicMaterial({ color: 0xff0000, linewidth: 20 });
    this.previewLine = new Line(geometry, material);
    
    // 添加到场景
    this.manager.sceneManager.scene.add(this.previewLine);
  }

  /*VR端生成预览线*/
  updatePreviewLineVR() {
    this.clearPreview();

    if (this.roadPoints.length < 2) return;

    // 1️⃣ 根据模式生成用于显示的 points
    const points = this.buildPreviewDisplayPoints();

    if (points.length < 2) return;

    // 2️⃣ 用 Tube 在 VR 中显示
    const curve = new CatmullRomCurve3(points);

    const geometry = new TubeGeometry(
      curve,
      points.length * 2,
      0.02,   // VR 可见半径
      8,
      false
    );

    const material = new MeshStandardMaterial({
      color: 0x00ff00,
      depthTest: false,
      transparent: true,
      opacity: 0.9
    });

    this.previewLine = new Mesh(geometry, material);
    console.log("VR端预览线",this.previewLine)
    this.previewLine.renderOrder = 999;

    // 稍微抬高
    this.previewLine.position.y += 0.03;

    this.manager.sceneManager.scene.add(this.previewLine);
  }

  buildPreviewDisplayPoints() {
    const result = [];

    // ===== 曲线模式 =====
    if (this.roadType === 'curve' && this.roadPoints.length > 2) {
      const curve = new CatmullRomCurve3(this.roadPoints);
      const curvePoints = curve.getPoints(
        Math.max(30, this.roadPoints.length * 10)
      );

      for (const p of curvePoints) {
        const y = this.getTerrainHeightAt(p.x, p.z, p.y);
        result.push(new Vector3(p.x, y, p.z));
      }

      return result;
    }

    // ===== 直线模式 =====
    for (let i = 0; i < this.roadPoints.length - 1; i++) {
      const start = this.roadPoints[i];
      const end = this.roadPoints[i + 1];
      const segments = 20;

      for (let j = 0; j <= segments; j++) {
        if (i > 0 && j === 0) continue;

        const t = j / segments;
        const x = start.x + (end.x - start.x) * t;
        const z = start.z + (end.z - start.z) * t;
        const y = this.getTerrainHeightAt(x, z, start.y);

        result.push(new Vector3(x, y, z));
      }
    }

    return result;
  }


  /**
   * 清除当前预览线
   */
  clearPreview() {
    if (this.previewLine) {
      this.manager.sceneManager.scene.remove(this.previewLine);
      this.previewLine.geometry.dispose();
      this.previewLine.material.dispose();
      this.previewLine = null;
    }
  }
  
  /**
   * 清除所有线段（包括固定线段和预览线）
   */
  clearAllLines() {
    // 清除当前预览线
    this.clearPreview();
    
    // 清除所有固定线段
    for (const line of this.fixedLines) {
      this.manager.sceneManager.scene.remove(line);
      line.geometry.dispose();
      line.material.dispose();
    }
    
    this.fixedLines = [];
  }

  /**
   * 完成道路绘制
   */
  finishRoad() {
    if (this.roadPoints.length < 2) return;
    
    // 固定当前预览线
    this.fixCurrentPreviewLine();
    
    // 创建最终的道路几何体
    this.createRoadMesh();
    
    if (this.roadMesh) {
      // 保存到道路列表
      this.roads.push(this.roadMesh);
      
      console.log("道路创建完成");
    }
    
    // 清除所有线段
    this.clearAllLines();
    
    // 重置状态
    this.isDrawing = false;
    this.roadPoints = [];
    this.previewPoints = [];
  }

  /**
   * 清除所有道路
   */
  clearAllRoads() {
    // 移除所有道路
    this.roads.forEach(road => {
      this.manager.sceneManager.scene.remove(road);
      road.geometry.dispose();
    });
    
    this.roads = [];
    
    // 清除所有线段
    this.clearAllLines();
    
    console.log("所有道路已清除");
  }

  /**
   * 清理资源
   */
  dispose() {
    this.disable();
    this.clearAllRoads();
    
    if (this.roadMaterial) {
      this.roadMaterial.dispose();
      this.roadMaterial = null;
    }
    
    this.terrainMeshes = [];
    this.manager = null;
  }
}